/**
 * usePrefilledFields — F-004 Phase 3 (IMPL-P3-002).
 *
 * React context che propaga la lista dei field-path "precompilati" (i path
 * restituiti da `prefillFromParent(parentDati, tipo).prefilledPaths`) dal host
 * (`AdempimentoInner`, Phase 4) ai singoli field component, senza prop-drilling.
 *
 * Mirror strutturale di `useErrorFields.tsx` (F-005), ma con semantica
 * "precompilato / modificato" (stile blu/info) invece di "contestato"
 * (stile amber/errore).
 *
 * A differenza di `useErrorFields`, questo hook lancia se usato fuori dal
 * Provider (mirror della shape di test `useBillingProfile`): un field che
 * consulta lo stato di prefill DEVE trovarsi dentro un `PrefilledFieldsProvider`,
 * mentre `useErrorFields` ha un default sicuro `[]` perché usato anche fuori
 * dai flussi di correzione. Qui il Provider è sempre montato dal host
 * adempimento, quindi l'assenza è un bug di integrazione da segnalare presto.
 *
 * NB: file `.tsx` perché esporta un componente React (Provider).
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

const PrefilledFieldsContext = createContext<string[] | null>(null);

export interface PrefilledFieldsProviderProps {
  children: ReactNode;
  /** Lista dei field path precompilati (`prefillFromParent().prefilledPaths`). */
  value: string[];
}

export function PrefilledFieldsProvider({ children, value }: PrefilledFieldsProviderProps) {
  return (
    <PrefilledFieldsContext.Provider value={value}>{children}</PrefilledFieldsContext.Provider>
  );
}

/**
 * Restituisce la lista dei field path precompilati dalla pratica madre.
 * Lancia se invocato fuori da `<PrefilledFieldsProvider>`.
 */
export function usePrefilledFields(): string[] {
  const ctx = useContext(PrefilledFieldsContext);
  if (ctx === null) {
    throw new Error('usePrefilledFields must be used inside <PrefilledFieldsProvider>');
  }
  return ctx;
}
